import React, { useState, useEffect } from 'react';
import { getStripe } from '../../utils/stripe';
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  PaymentRequestButtonElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import styled, { keyframes } from 'styled-components';
import { Lock, Smartphone, CreditCard, Wifi } from 'lucide-react';
import { formatShopPrice } from '../../utils/currency';

// ─── Styled Components ───────────────────────────────────────────────────────

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme?.textMuted || '#888'};
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const WalletSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const WalletButtonWrapper = styled.div`
  border-radius: 8px;
  overflow: hidden;
  min-height: 48px;
  position: relative;
`;

const WalletUnavailableBox = styled.div`
  background: ${({ theme }) => theme?.inputBg || 'rgba(255,255,255,0.05)'};
  border: 1.5px dashed ${({ theme }) => theme?.border || 'rgba(255,255,255,0.15)'};
  border-radius: 8px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: default;
`;

const WalletIcons = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  flex-shrink: 0;
`;

const WalletIconBadge = styled.div`
  background: ${({ theme }) => theme?.cardBg || 'rgba(255,255,255,0.08)'};
  border: 1px solid ${({ theme }) => theme?.border || 'rgba(255,255,255,0.1)'};
  border-radius: 5px;
  padding: 3px 7px;
  font-size: 10px;
  font-weight: 700;
  color: ${({ theme }) => theme?.textMuted || '#aaa'};
  letter-spacing: 0.02em;
  white-space: nowrap;
`;

const WalletUnavailableText = styled.div`
  flex: 1;
`;

const WalletUnavailableTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme?.text || '#ddd'};
  margin-bottom: 2px;
`;

const WalletUnavailableSubtitle = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme?.textMuted || '#888'};
  line-height: 1.4;
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 2px 0;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${({ theme }) => theme?.border || 'rgba(255,255,255,0.1)'};
  }

  span {
    font-size: 11px;
    color: ${({ theme }) => theme?.textMuted || '#777'};
    white-space: nowrap;
    font-weight: 500;
  }
`;

const CardSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CardRow = styled.div`
  display: grid;
  grid-template-columns: ${({ cols }) => cols || '1fr'};
  gap: 8px;
`;

const StripeInputWrapper = styled.div`
  background: ${({ theme }) => theme?.inputBg || 'rgba(255,255,255,0.07)'};
  border: 1.5px solid ${({ theme, focused }) =>
    focused ? (theme?.accent || '#6c63ff') : (theme?.border || 'rgba(255,255,255,0.12)')};
  border-radius: 8px;
  padding: 12px 14px;
  transition: border-color 0.15s ease;
`;

const ErrorText = styled.div`
  font-size: 11px;
  color: #ff6b6b;
  margin-top: 2px;
  padding-left: 2px;
`;

const PayButton = styled.button`
  width: 100%;
  padding: 13px;
  background: ${({ theme }) => theme?.accent || '#6c63ff'};
  color: white;
  border: none;
  border-radius: 9px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 4px;
  transition: opacity 0.15s ease, transform 0.1s ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:not(:disabled):hover {
    opacity: 0.92;
    transform: translateY(-1px);
  }

  &:not(:disabled):active {
    transform: translateY(0);
  }
`;

const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;
const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;

const SecureNote = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  font-size: 11px;
  color: ${({ theme }) => theme?.textMuted || '#777'};
  margin-top: 2px;
`;

// ─── Stripe Element appearance ────────────────────────────────────────────────

const getStripeAppearance = (theme) => ({
  color: theme?.text || '#fff',
  fontSize: '14px',
  fontFamily: 'inherit',
  '::placeholder': { color: theme?.textMuted || '#666' },
});

// ─── Inner form ───────────────────────────────────────────────────────────────

const isLocalhostEnv =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

function genTransactionCode() {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  return 'KODE-' + Array.from({ length: 6 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

async function localMockPayment({ transactionId, chatId, buyerInfo, amount }) {
  const { db } = await import('../../firebase/config');
  const {
    doc, getDoc, updateDoc, addDoc, collection,
    serverTimestamp, increment,
  } = await import('firebase/firestore');

  const txSnap = await getDoc(doc(db, 'transactions', transactionId));
  const tx = txSnap.exists() ? txSnap.data() : {};
  const resolvedChatId = chatId || tx.chatId || transactionId;
  const sellerId = tx.sellerId;

  const code = genTransactionCode();
  const qrCodeUrl = `https://kalkode.com/messages?chat=${resolvedChatId}&code=${code}`;

  await updateDoc(doc(db, 'transactions', transactionId), {
    status: 'paid',
    paymentStatus: 'authorized',
    useMockStripe: true,
    stripePaymentIntentId: 'mock_pi_local_' + Date.now(),
    transactionCode: code,
    qrCodeUrl,
    paidAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    paymentMethodDetails: { type: 'card', brand: 'visa', last4: '4242' },
  });

  await addDoc(collection(db, 'chats', resolvedChatId, 'messages'), {
    text: 'Payment successful! Show this QR code to the seller for pickup:',
    sender: 'system',
    senderName: 'System',
    timestamp: serverTimestamp(),
    type: 'payment_qr_code',
    visibleTo: [buyerInfo?.uid].filter(Boolean),
    purchaseData: {
      transactionCode: code,
      qrCodeUrl,
      approvedQuantity: tx.approvedQuantity || tx.requestedQuantity,
      total: amount,
      buyerId: buyerInfo?.uid,
      isGuest: buyerInfo?.userType === 'guest',
    },
  });

  await updateDoc(doc(db, 'chats', resolvedChatId), {
    status: 'paid',
    transactionStatus: 'paid',
    lastMessage: 'Payment completed - QR code generated',
    lastMessageTime: serverTimestamp(),
    ...(sellerId ? { [`unreadCount.${sellerId}`]: increment(1) } : {}),
    updatedAt: serverTimestamp(),
  });

  return { transactionCode: code, qrCodeUrl };
}

// ─── Inner form ───────────────────────────────────────────────────────────────

const PaymentFormInner = ({
  amount,
  currency = 'USD',
  transactionId,
  chatId,
  sellerId,
  buyerInfo,
  onSuccess,
  onCancel,
  theme,
  mode = 'all', // 'all' | 'card' | 'wallet'
  nfcData = null,
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const [paymentRequest, setPaymentRequest] = useState(null);
  const [walletAvailable, setWalletAvailable] = useState(null);
  const [focused, setFocused] = useState({});
  const [cardError, setCardError] = useState('');
  const [processing, setProcessing] = useState(false);

  const amountCents = Math.round(amount * 100);

  // ── Set up Payment Request (wallet detection) ──────────────────────────────
  useEffect(() => {
    if (!stripe || !amount) return;

    const pr = stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: { label: 'Order Total', amount: amountCents },
      requestPayerName: true,
      requestPayerEmail: false,
    });

    pr.canMakePayment().then((result) => {
      setWalletAvailable(!!result);
      if (result) setPaymentRequest(pr);
    });

    pr.on('paymentmethod', async (ev) => {
      if (isLocalhostEnv) {
        try {
          const { transactionCode, qrCodeUrl } = await localMockPayment({ transactionId, chatId, buyerInfo, amount });
          ev.complete('success');
          onSuccess?.(transactionCode, qrCodeUrl);
        } catch {
          ev.complete('fail');
        }
        return;
      }
      try {
        const res = await fetch('/api/create-payment-intent', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            amount,
            transactionId,
            sellerId,
            buyerId: buyerInfo?.uid || null,
            buyerName: buyerInfo?.name || 'Buyer',
            userType: buyerInfo?.userType || 'authenticated',
            guestSession: buyerInfo?.guestSession || null,
            paymentMethodId: ev.paymentMethod.id,
          }),
        });
        const data = await res.json();
        const { error: serverError, success, transactionCode, qrCodeUrl, paymentIntent: piData } = data;

        if (serverError) { ev.complete('fail'); return; }

        // Mock or server-side pre-confirmed — no client action needed
        if (success && (!piData?.client_secret || piData?.status === 'requires_capture' || piData?.status === 'succeeded')) {
          ev.complete('success');
          onSuccess?.(transactionCode, qrCodeUrl);
          return;
        }

        // 3DS required
        const { error: confirmError } = await stripe.confirmCardPayment(
          piData.client_secret,
          { payment_method: ev.paymentMethod.id },
          { handleActions: false }
        );

        if (confirmError) {
          ev.complete('fail');
        } else {
          ev.complete('success');
          onSuccess?.(transactionCode, qrCodeUrl);
        }
      } catch {
        ev.complete('fail');
      }
    });
  }, [stripe, amountCents]);

  // ── Auto-process when NFC data arrives with a payment token ───────────────
  useEffect(() => {
    if (!nfcData || !stripe || !elements || processing) return;
    if (nfcData.token) {
      // NDEF tag carried an explicit payment token — confirm directly
      handleNfcTokenPay(nfcData.token);
    }
    // If no token, the card form stays open for manual confirmation
    // (nfcData.serialNumber still proves physical card presence)
  }, [nfcData, stripe, elements]);

  const handleNfcTokenPay = async (token) => {
    setProcessing(true);
    setCardError('');

    if (isLocalhostEnv) {
      try {
        const { transactionCode, qrCodeUrl } = await localMockPayment({ transactionId, chatId, buyerInfo, amount });
        onSuccess?.(transactionCode, qrCodeUrl);
      } catch (e) {
        setCardError('Local mock payment failed: ' + e.message);
      } finally {
        setProcessing(false);
      }
      return;
    }

    try {
      const res = await fetch('/api/create-payment-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount,
          transactionId,
          sellerId,
          buyerId: buyerInfo?.uid || null,
          buyerName: buyerInfo?.name || 'Buyer',
          userType: buyerInfo?.userType || 'authenticated',
          guestSession: buyerInfo?.guestSession || null,
          paymentMethodId: token,
          nfcCapture: true,
        }),
      });
      const data = await res.json();
      const { error: serverError, success, transactionCode, qrCodeUrl, paymentIntent: piData } = data;
      if (serverError) { setCardError(serverError); setProcessing(false); return; }

      if (success && (!piData?.client_secret || piData?.status === 'requires_capture' || piData?.status === 'succeeded')) {
        onSuccess?.(transactionCode, qrCodeUrl);
        return;
      }

      const { error: confirmError } = await stripe.confirmCardPayment(piData.client_secret);
      if (confirmError) { setCardError(confirmError.message); }
      else { onSuccess?.(transactionCode, qrCodeUrl); }
    } catch (e) {
      setCardError('NFC payment failed. Please enter card details manually.');
    } finally {
      setProcessing(false);
    }
  };

  // ── Card payment ───────────────────────────────────────────────────────────
  const handleCardPay = async () => {
    if (!stripe || !elements) return;
    setProcessing(true);
    setCardError('');

    // On localhost Vercel functions aren't available — write mock directly to Firestore
    if (isLocalhostEnv) {
      try {
        const { transactionCode, qrCodeUrl } = await localMockPayment({ transactionId, chatId, buyerInfo, amount });
        onSuccess?.(transactionCode, qrCodeUrl);
      } catch (e) {
        setCardError('Local mock payment failed: ' + e.message);
      } finally {
        setProcessing(false);
      }
      return;
    }

    try {
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardNumberElement),
        billing_details: { name: buyerInfo?.name || 'Buyer' },
      });
      if (pmError) { setCardError(pmError.message); setProcessing(false); return; }

      const res = await fetch('/api/create-payment-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount,
          transactionId,
          sellerId,
          buyerId: buyerInfo?.uid || null,
          buyerName: buyerInfo?.name || 'Buyer',
          userType: buyerInfo?.userType || 'authenticated',
          guestSession: buyerInfo?.guestSession || null,
          paymentMethodId: paymentMethod.id,
        }),
      });

      const data = await res.json();
      const { error: serverError, success, transactionCode, qrCodeUrl, paymentIntent: piData } = data;
      if (serverError) { setCardError(serverError); setProcessing(false); return; }

      // Mock or server-side pre-confirmed (requires_capture / succeeded) — no client action needed
      if (success && (!piData?.client_secret || piData?.status === 'requires_capture' || piData?.status === 'succeeded')) {
        onSuccess?.(transactionCode, qrCodeUrl);
        return;
      }

      // 3DS authentication required
      const { error: confirmError } = await stripe.confirmCardPayment(piData.client_secret);
      if (confirmError) {
        setCardError(confirmError.message);
      } else {
        onSuccess?.(transactionCode, qrCodeUrl);
      }
    } catch (e) {
      setCardError('Payment failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const inputAppearance = getStripeAppearance(theme);

  const showWallet = mode === 'all' || mode === 'wallet';
  const showCard   = mode === 'all' || mode === 'card';

  return (
    <FormContainer>

      {/* ── Wallet Section ─────────────────────────────────────── */}
      {showWallet && (
        <WalletSection>
          {walletAvailable === null && (
            <WalletButtonWrapper style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 8, height: 48 }} />
          )}
          {walletAvailable === true && paymentRequest && (
            <WalletButtonWrapper>
              <PaymentRequestButtonElement
                options={{
                  paymentRequest,
                  style: { paymentRequestButton: { type: 'default', theme: 'dark', height: '48px' } },
                }}
              />
            </WalletButtonWrapper>
          )}
          {walletAvailable === false && (
            <WalletUnavailableBox>
              <WalletIcons>
                <WalletIconBadge>Apple Pay</WalletIconBadge>
                <WalletIconBadge>G Pay</WalletIconBadge>
              </WalletIcons>
              <WalletUnavailableText>
                <WalletUnavailableSubtitle>
                  Use Safari on iOS or Chrome on Android to pay with Apple/Google Pay
                </WalletUnavailableSubtitle>
              </WalletUnavailableText>
            </WalletUnavailableBox>
          )}
        </WalletSection>
      )}

      {showWallet && showCard && <Divider><span>or</span></Divider>}

      {/* ── Card Section ───────────────────────────────────────── */}
      {showCard && (
        <>
          <CardSection>
            <SectionLabel>
              <CreditCard size={13} />
              Card Details
            </SectionLabel>

            <StripeInputWrapper focused={focused.number} theme={theme}>
              <CardNumberElement
                options={{ style: { base: inputAppearance }, showIcon: true, disableLink: true }}
                onFocus={() => setFocused(f => ({ ...f, number: true }))}
                onBlur={() => setFocused(f => ({ ...f, number: false }))}
                onChange={(e) => e.error && setCardError(e.error.message)}
              />
            </StripeInputWrapper>

            <CardRow cols="1fr 1fr">
              <StripeInputWrapper focused={focused.expiry} theme={theme}>
                <CardExpiryElement
                  options={{ style: { base: inputAppearance }, disableLink: true }}
                  onFocus={() => setFocused(f => ({ ...f, expiry: true }))}
                  onBlur={() => setFocused(f => ({ ...f, expiry: false }))}
                />
              </StripeInputWrapper>
              <StripeInputWrapper focused={focused.cvc} theme={theme}>
                <CardCvcElement
                  options={{ style: { base: inputAppearance }, disableLink: true }}
                  onFocus={() => setFocused(f => ({ ...f, cvc: true }))}
                  onBlur={() => setFocused(f => ({ ...f, cvc: false }))}
                />
              </StripeInputWrapper>
            </CardRow>

            {cardError && <ErrorText>{cardError}</ErrorText>}
          </CardSection>

          <PayButton onClick={handleCardPay} disabled={processing || !stripe}>
            {processing ? <Spinner /> : <><Lock size={14} /> Pay {formatShopPrice(amount, currency)}</>}
          </PayButton>
        </>
      )}

      <SecureNote>
        <Lock size={10} />
        Payments secured by Stripe
      </SecureNote>

    </FormContainer>
  );
};

// ─── Exported wrapper ─────────────────────────────────────────────────────────

const StripePaymentForm = (props) => {
  const { theme, ...rest } = props;
  const [stripePromise, setStripePromise] = useState(null);

  useEffect(() => { setStripePromise(getStripe()); }, []);

  const stripeElementsOptions = {
    fonts: [{ cssSrc: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500&display=swap' }],
    wallets: { link: 'never' },
  };

  return (
    <Elements stripe={stripePromise} options={stripeElementsOptions}>
      <PaymentFormInner theme={theme} {...rest} />
    </Elements>
  );
};

export default StripePaymentForm;